/**
 * Edit Distributor Form Composer
 */

import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  EditDistributorFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type EditDistributorFormData
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import { renderTextControl, renderButtonControl, type ControlConfig } from '../../formRenderFunctions'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Truck } from 'lucide-react'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

export type EditDistributorComposerProps = {
  distributorNumber: number
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues: Partial<EditDistributorFormData>
  showHeader?: boolean
  submitLabel?: string
}

const validateField = (name: keyof EditDistributorFormData, value: unknown): string | null => {
  const shape = EditDistributorFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

export function EditDistributorComposer(props: EditDistributorComposerProps) {
  const { distributorNumber, onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const updateDistributor = useApiMutation('upsertDeviceDistributor', 'POST', {
    invalidates: ['getDeviceDistributors'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to update distributor:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: EditDistributorFormSchema,
    defaultValues: {
      distributorNumber: distributorNumber,
      distributorName: initialValues?.distributorName ?? '',
    },
    onSubmit: async ({ value }) => {
      const formValue = value as EditDistributorFormData
      const parsed = EditDistributorFormSchema.safeParse({
        distributorNumber: distributorNumber,
        distributorName: formValue.distributorName?.trim() ?? '',
      })

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string' && (fieldName === 'distributorNumber' || fieldName === 'distributorName')) {
            form.setFieldMeta(fieldName as keyof EditDistributorFormData, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        return
      }

      const apiRequest = transformToApiRequest(parsed.data)
      await updateDistributor.mutateAsync(apiRequest)
    },
  })

  const renderControl = (control: any) => {
    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) => validateField(control.name as keyof EditDistributorFormData, value) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: true,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []
      if (groupControls.length === 0) return null
      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map(renderControl)}
        </div>
      )
    })
  }

  const formBody = (
    <>
      {renderFormContent()}
      <div className="pt-4 flex justify-center gap-3">
        {renderButtonControl({
          label: FORM_CONFIG.cancelButton?.label ?? '',
          variant: 'outline',
          onClick: onCancel,
          visibility: !!onCancel,
          type: 'button'
        })}

        {renderButtonControl({
          label: updateDistributor.isPending
            ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Updating...')
            : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Update'),
          variant: 'default',
          type: 'submit'
        }, { SubmitButton: form.SubmitButton })}
      </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Truck className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}
    </form.AppForm>
  )
}

export {
  EditDistributorFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type EditDistributorFormData
} from './schema'
